import { createPortal } from 'react-dom';
import { useDiscoverDomains, useJoinDomain } from '../../../hooks/useDomains';
import { useDomainContext } from '../../../contexts/DomainContext';
import { GlassButton } from '../GlassButton';

interface Props {
    onClose: () => void;
}

export function DiscoverDomainsModal({ onClose }: Props) {
    const { data: domains, isLoading, isError } = useDiscoverDomains();
    const joinMutation = useJoinDomain();
    const { setActiveDomainId } = useDomainContext();

    const handleJoin = async (id: string) => {
        try {
            await joinMutation.mutateAsync(id);
            setActiveDomainId(id);
            onClose();
        } catch (error) {
            console.error('Failed to join domain:', error);
            alert('Failed to join domain. Please try again.');
        }
    };

    return createPortal(
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.7)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1100
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: 'var(--bg-panel)',
                    padding: '2rem',
                    borderRadius: '12px',
                    width: '500px',
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    border: '1px solid var(--surface-glass-border)',
                    boxShadow: 'var(--shadow-modal-lg)',
                    color: 'var(--text-primary)'
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h2 style={{ margin: 0 }}>🌐 Discover Shared Desks</h2>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.5rem' }}>&times;</button>
                </div>

                {isLoading && <div>Searching for available desks...</div>}
                {isError && <div style={{ color: 'var(--accent-danger)' }}>Error loading available desks.</div>}

                {!isLoading && domains && domains.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                        No new shared desks available to join right now.
                    </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {domains?.map(d => (
                        <div key={d.id} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '1rem',
                            backgroundColor: 'var(--bg-card)',
                            borderRadius: '8px',
                            border: '1px solid var(--surface-glass-border)'
                        }}>
                            <div>
                                <div style={{ fontWeight: 'bold' }}>{d.name}</div>
                                {d.created_by && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Created by {d.created_by}</div>}
                            </div>
                            <GlassButton
                                onClick={() => handleJoin(d.id)}
                                disabled={joinMutation.isPending}
                            >
                                {joinMutation.isPending ? 'Joining...' : 'Join Desk'}
                            </GlassButton>
                        </div>
                    ))}
                </div>

                <div style={{ marginTop: '2rem', textAlign: 'right' }}>
                    <GlassButton onClick={onClose} variant="ghost">Close</GlassButton>
                </div>
            </div>
        </div>,
        document.body
    );
}
